/**
 * HeatmapTable — students (rows) × skills (columns) with cell color
 * encoding mastery. Null cells render as a neutral "未评估".
 */
import type { ClassHeatmap } from "../api/school";

const SKILL_LABEL: Record<string, string> = {
  listening: "听力",
  speaking: "口语",
  reading: "阅读",
  writing: "写作",
  vocabulary: "词汇",
  grammar: "语法",
};

function cellColor(score: number | null): string {
  if (score === null) return "bg-slate-100 text-slate-400";
  if (score >= 85) return "bg-emerald-100 text-emerald-900";
  if (score >= 70) return "bg-sky-100 text-sky-900";
  if (score >= 60) return "bg-amber-100 text-amber-900";
  return "bg-rose-100 text-rose-900";
}

export function HeatmapTable({ data }: { data: ClassHeatmap }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-3 py-2 font-medium w-32 sticky left-0 bg-slate-50">
              学生
            </th>
            {data.skills.map((s) => (
              <th key={s} className="text-center px-3 py-2 font-medium">
                {SKILL_LABEL[s] ?? s}
              </th>
            ))}
            <th className="text-center px-3 py-2 font-medium">综合</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <tr key={row.student_id} className="border-t border-slate-100">
              <td className="px-3 py-2 text-slate-800 whitespace-nowrap sticky left-0 bg-white">
                {row.name_zh}
                {row.name_en && (
                  <span className="text-slate-400 text-xs ml-1">({row.name_en})</span>
                )}
              </td>
              {data.skills.map((s) => {
                const v = row.skills[s] ?? null;
                return (
                  <td key={s} className="px-2 py-1 text-center">
                    <span
                      className={`inline-block px-2 py-1 rounded text-xs font-mono ${cellColor(v)}`}
                    >
                      {v ?? "—"}
                    </span>
                  </td>
                );
              })}
              <td className="px-2 py-1 text-center">
                <span
                  className={`inline-block px-2 py-1 rounded text-xs font-mono ${cellColor(row.overall)}`}
                >
                  {row.overall ?? "—"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
